import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetProjectKeyDetailsById } from "../../api/services/project/projectApis";
import {
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiButtonTertiary,
} from "../../components";
import AccessDenied from "../../components/accessDenied/AccessDenied";
import { PAGE_IDS, PAGE_URLS } from "../../helpers/constants";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import {
  checkIsStatusCode403Forbidden,
  getValueOrBlank,
  isNotEmpty,
} from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { projectCountryPermissionSelector } from "../../redux/slices/project/projectSelector";
import {
  saveKeyProjectDetails,
  updateProjectCountryPermission,
  updateProjectOpcoFieldsEditable,
} from "../../redux/slices/project/projectSlice";
import { ViewProjectWrapper } from "./ViewProject.style";
import ProjectNavigation from "./components/projectNavigation/ProjectNavigation";
import ViewProjectHeader from "./components/viewProjectHeader/ViewProjectHeader";
import { ViewProjectKeyDetails } from "./components/viewProjectKeyDetails/ViewProjectKeyDetails";

const pageId = PAGE_IDS.VIEW_PROJECT;
const ViewProject: React.FC = () => {
  const dispatch = useDispatch();
  const isEditable = useSelector(projectCountryPermissionSelector);
  const methods = useForm({
    mode: "all",
  });
  const { id = "" } = useParams();
  const { commonLabels } = useSiteCore();
  const location = useLocation();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const [selectedTab, setSelectedTab] = useState(0);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const { id: projectId } = useParams() as { id: string };

  const isNavigatedFromProgram =
    location?.state?.from === PAGE_URLS.EDIT_PROGRAM ||
    location?.state?.from === PAGE_URLS.VIEW_PROGRAM;

  const isNavigatedFromViewProgram =
    location?.state?.from === PAGE_URLS.VIEW_PROGRAM;

  const programId = location?.state?.programId || "";

  const {
    data: projectKeyDetailsResponse,
    isFetching: isGetKeyProjectPending,
    isError: isGetKeyProjectError,
    error: getKeyProjectError,
    refetchWithParams: refetchProjectKeyDetail,
  } = useGetProjectKeyDetailsById();

  useEffect(() => {
    if (isNotEmpty(projectId)) {
      refetchProjectKeyDetail(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    if (isNotEmpty(projectKeyDetailsResponse)) {
      dispatch(
        updateProjectCountryPermission(
          projectKeyDetailsResponse?.isEditable ?? false,
        ),
      );
    }
  }, [projectKeyDetailsResponse]);

  useEffect(() => {
    if (isNotEmpty(projectKeyDetailsResponse)) {
      dispatch(
        saveKeyProjectDetails({
          ...projectKeyDetailsResponse,
          id: projectId,
        }),
      );
      dispatch(
        updateProjectOpcoFieldsEditable(
          !!projectKeyDetailsResponse.areOpcoFieldsEditable,
        ),
      );
    }
  }, [projectKeyDetailsResponse]);

  //   Loader
  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isGetKeyProjectPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isGetKeyProjectPending]);

  useEffect(() => {
    if (isGetKeyProjectError && getKeyProjectError) {
      if (checkIsStatusCode403Forbidden(getKeyProjectError as AxiosError)) {
        setShowAccessDenied(true);
      } else {
        displayToast(
          commonLabels.someThingWentWrongPleaseTryAgainLater,
          "error",
        );
      }
      dispatch(toggleLoader({ isVisible: false }));
    } else {
      setShowAccessDenied(false);
    }
  }, [isGetKeyProjectError, getKeyProjectError]);

  const handleEditProject = (projectId: string) => {
    type StateType = { tabValue: number; from?: string };
    const state: StateType = { tabValue: selectedTab };

    navigate(`${PAGE_URLS.EDIT_PROJECT}/${projectId}`, {
      state: { ...location?.state, ...state },
    });
  };

  useEffect(() => {
    dispatch(updateProjectCountryPermission(false));
  }, []);

  const handleBackNavigation = () => {
    if (isNavigatedFromProgram) {
      navigate(
        `${isNavigatedFromViewProgram ? PAGE_URLS.VIEW_PROGRAM : PAGE_URLS.EDIT_PROGRAM}/${programId}`,
        { replace: true },
      );
    } else {
      navigate(PAGE_URLS.SEARCH_PROJECT, { replace: true });
    }
  };

  if (showAccessDenied) {
    return <AccessDenied />;
  }

  return (
    <FormProvider {...methods}>
      <ViewProjectWrapper>
        {isNavigatedFromProgram && (
          <MuiButtonTertiary
            data-testid={`${pageId}-back`}
            onClick={handleBackNavigation}
          >
            <ArrowBackIosIcon />
            {commonLabels.back}
          </MuiButtonTertiary>
        )}
        <ViewProjectHeader
          projectId={getValueOrBlank(id)}
          onEditClick={() => handleEditProject(id)}
        />
        <ViewProjectKeyDetails />
        <ProjectNavigation
          pageId={pageId}
          pageUrl={PAGE_URLS.VIEW_PROJECT}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          isNavigatedFromProgram={isNavigatedFromProgram}
        />
        <MuiBox className="bottom-button-container">
          <MuiButtonSecondary
            id={COMPONENT_IDS.CLOSE_BUTTON}
            className="bottom-button-close"
            onClick={handleBackNavigation}
          >
            {commonLabels.close}
          </MuiButtonSecondary>
          {isEditable && (
            <MuiButtonPrimary
              id={COMPONENT_IDS.CLOSE_BUTTON}
              className="bottom-button-edit"
              onClick={() => handleEditProject(id)}
            >
              {commonLabels.edit}
            </MuiButtonPrimary>
          )}
        </MuiBox>
      </ViewProjectWrapper>
    </FormProvider>
  );
};

export default ViewProject;
